import { useState } from 'react'
import { authClient } from '@/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/Logo'
import { Mail, Lock, ArrowRight } from 'lucide-react'
import { env } from '@/config/env'

const DEV_USER = { email: 'user@buncheck.dev', password: 'password123' }

interface LoginScreenProps {
  className?: string
  redirectTo?: string
}

export function LoginScreen({ className, redirectTo }: LoginScreenProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleDevLogin = async () => {
    setIsLoading(true)
    setError('')
    try {
      const result = await authClient.signIn.email(DEV_USER)
      if (result.error) {
        setError(result.error.message || 'Dev login failed')
      } else {
        window.location.href = redirectTo || '/'
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Dev login failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const result = await authClient.signIn.email({
        email,
        password,
      })

      if (result.error) {
        setError(result.error.message || 'Sign in failed')
      } else {
        // Redirect to home or specified path
        window.location.href = redirectTo || '/'
      }
    } catch (err) {
      console.error('Sign in error:', err)
      setError(err instanceof Error ? err.message : 'Sign in failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      className={cn(
        'min-h-screen flex items-center justify-center bg-background p-4',
        className
      )}
    >
      <div className="w-full max-w-lg">
        {/* Card */}
        <div className="bg-card rounded-3xl shadow-xl overflow-hidden border">
          {/* Brand Header */}
          <div className="bg-forest p-8 text-center">
            <div className="flex justify-center mb-4">
              <Logo variant="bun" className="h-16" />
            </div>
            <h1 className="text-2xl font-bold text-cream">Welcome back</h1>
            <p className="text-cream/80 mt-1">Sign in to continue to BunCheck</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  data-test-id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={isLoading}
                  className="pl-10 h-12 rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  data-test-id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={isLoading}
                  className="pl-10 h-12 rounded-xl"
                />
              </div>
            </div>

            {error && (
              <div data-test-id="login-error" className="bg-destructive/10 text-destructive text-sm p-3 rounded-xl">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-test-id="login-submit"
              className="w-full h-12 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
              disabled={isLoading}
            >
              {isLoading ? (
                'Signing in...'
              ) : (
                <span className="flex items-center gap-2">
                  Sign in
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="px-8 pb-8 text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <a
                href="/auth/signup"
                data-test-id="login-signup-link"
                className="text-primary hover:text-primary/80 font-medium underline-offset-4 hover:underline"
              >
                Create one
              </a>
            </p>
            {env === 'development' && (
              <Button
                type="button"
                variant="outline"
                className="w-full h-10 rounded-xl text-sm"
                onClick={handleDevLogin}
                disabled={isLoading}
              >
                Dev Login
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
